import { addDays, addMinutes, format, isValid, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  CustomSlotConfig,
  DisplaySlot,
  SlotItem,
  WEEKDAY_BY_INDEX,
} from './slotTypes';

const FALLBACK_TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata',
  'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney',
];

export interface TimezoneOption {
  value: string;
  label: string;
}

let timezoneCache: TimezoneOption[] | null = null;

export const getTimezoneOptions = (): TimezoneOption[] => {
  if (timezoneCache) return timezoneCache;

  let zones: string[] = FALLBACK_TIMEZONES;
  try {
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      zones = (Intl as any).supportedValuesOf('timeZone');
    }
  } catch {
    zones = FALLBACK_TIMEZONES;
  }

  timezoneCache = zones.map((zone) => {
    try {
      const offset = formatInTimeZone(new Date(), zone, 'XXX');
      return { value: zone, label: `(UTC${offset}) ${zone.replace(/_/g, ' ')}` };
    } catch {
      return { value: zone, label: zone };
    }
  });
  return timezoneCache;
};

/** Replaces `{{name}}` with the page's `?name=` query param, so one form can serve many schedules. */
export const resolveUrlTemplate = (url: string, params: URLSearchParams): string =>
  url.replace(/\{\{\s*([\w.-]+)\s*\}\}/g, (_, key) => encodeURIComponent(params.get(key) ?? ''));

export const getUrlPlaceholders = (url: string): string[] =>
  Array.from(new Set(Array.from(url.matchAll(/\{\{\s*([\w.-]+)\s*\}\}/g), ([, key]) => key)));

export const hasUnresolvedPlaceholder = (url: string, params: URLSearchParams): boolean =>
  getUrlPlaceholders(url).some((key) => !params.get(key));

const getByPath = (source: any, path: string): any =>
  path
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc == null ? undefined : acc[key]), source);

const COMMON_LIST_KEYS = ['schedule', 'slots', 'data', 'items', 'results'];
const START_KEYS = ['start', 'startTime', 'start_time', 'startAt', 'time', 'datetime', 'date', 'slot'];
const END_KEYS = ['end', 'endTime', 'end_time', 'endAt'];

const pickFirst = (item: any, keys: string[]): any => {
  const key = keys.find((k) => item?.[k] != null);
  return key ? item[key] : undefined;
};

const toIso = (value: any): string | null => {
  if (value == null || value === '') return null;
  const date = new Date(value);
  return isValid(date) ? date.toISOString() : null;
};

/**
 * Accepts a bare array or an object holding one, with items as ISO strings or
 * objects like `{ start, end, available }`, and returns a sorted, de-duplicated list.
 */
export const normalizeApiSlots = (response: any, path: string): SlotItem[] => {
  let list = path ? getByPath(response, path) : response;
  if (!Array.isArray(list) && !path) {
    const key = COMMON_LIST_KEYS.find((k) => Array.isArray(response?.[k]));
    list = key ? response[key] : undefined;
  }
  if (!Array.isArray(list)) return [];

  const seen = new Set<string>();
  const slots: SlotItem[] = [];
  list.forEach((item: any) => {
    if (item && typeof item === 'object' && item.available === false) return;
    const raw = typeof item === 'object' ? pickFirst(item, START_KEYS) : item;
    const iso = toIso(raw);
    if (!iso || seen.has(iso)) return;
    seen.add(iso);
    const endIso = typeof item === 'object' ? toIso(pickFirst(item, END_KEYS)) : null;
    slots.push(endIso ? { iso, endIso } : { iso });
  });
  return slots.sort((a, b) => a.iso.localeCompare(b.iso));
};

const toMinutes = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** Builds bookable slots from weekly hours, in the host's timezone, for the given range. */
export const generateCustomSlots = (
  config: CustomSlotConfig,
  rangeStart: Date,
  rangeEnd: Date,
  now: Date = new Date(),
): SlotItem[] => {
  const duration = Math.max(5, Number(config.durationMinutes) || 30);
  const step = duration + Math.max(0, Number(config.bufferMinutes) || 0);
  const earliest = addMinutes(now, Math.max(0, Number(config.minNoticeHours) || 0) * 60);
  const latest = config.maxDaysAhead > 0 ? addDays(now, Number(config.maxDaysAhead)) : null;
  const blocked = new Set(config.blockedDates);
  const slots: SlotItem[] = [];

  for (let day = rangeStart; day <= rangeEnd; day = addDays(day, 1)) {
    const dateKey = format(day, 'yyyy-MM-dd');
    if (blocked.has(dateKey)) continue;

    const hours = config.weeklyHours[WEEKDAY_BY_INDEX[day.getDay()]];
    if (!hours?.enabled) continue;

    const windowEnd = toMinutes(hours.end);
    for (let start = toMinutes(hours.start); start + duration <= windowEnd; start += step) {
      const hh = String(Math.floor(start / 60)).padStart(2, '0');
      const mm = String(start % 60).padStart(2, '0');
      const startDate = fromZonedTime(`${dateKey}T${hh}:${mm}:00`, config.timezone);
      if (startDate < earliest) continue;
      if (latest && startDate > latest) continue;
      slots.push({
        iso: startDate.toISOString(),
        endIso: addMinutes(startDate, duration).toISOString(),
      });
    }
  }
  return slots;
};

export const dayKeyInZone = (iso: string, timezone: string): string =>
  formatInTimeZone(new Date(iso), timezone, 'yyyy-MM-dd');

export const groupSlotsByDay = (
  slots: SlotItem[],
  timezone: string,
): Map<string, SlotItem[]> => {
  const byDay = new Map<string, SlotItem[]>();
  slots.forEach((slot) => {
    const key = dayKeyInZone(slot.iso, timezone);
    const bucket = byDay.get(key);
    if (bucket) bucket.push(slot);
    else byDay.set(key, [slot]);
  });
  return byDay;
};

export const toDisplaySlots = (
  slots: SlotItem[],
  timezone: string,
  use24Hour: boolean,
): DisplaySlot[] =>
  slots.map((slot) => ({
    ...slot,
    label: formatInTimeZone(new Date(slot.iso), timezone, use24Hour ? 'HH:mm' : 'h:mm a'),
  }));

export const formatSlotSummary = (iso: string, timezone: string, use24Hour: boolean): string =>
  formatInTimeZone(
    new Date(iso),
    timezone,
    use24Hour ? 'EEEE, dd MMM yyyy, HH:mm' : 'EEEE, dd MMM yyyy, h:mm a',
  );

export const parseDayKey = (key: string): Date | null => {
  const parsed = parse(key, 'yyyy-MM-dd', new Date());
  return isValid(parsed) ? parsed : null;
};
